import type { MatchRecord } from './api';

const endpoint = (import.meta.env.VITE_REALTIME_URL || '') as string;

export type RealtimeStatus = 'connecting' | 'live' | 'reconnecting' | 'offline';
export type RealtimeMessage =
  | { type: 'match'; match: MatchRecord; serverNow: number; connected?: boolean; acceptedMove?: string }
  | { type: 'error'; code: string; message: string; match?: MatchRecord }
  | { type: 'pong'; serverNow: number };

export const realtimeConfigured = Boolean(endpoint);

export class MatchRealtimeClient {
  private socket: WebSocket | null = null;
  private retry: number | null = null;
  private heartbeat: number | null = null;
  private attempts = 0;
  private closed = false;

  constructor(
    private readonly profileId: string,
    private readonly matchId: string,
    private readonly onMessage: (message: RealtimeMessage) => void,
    private readonly onStatus: (status: RealtimeStatus) => void,
  ) {}

  connect() {
    if (this.closed || !endpoint) return;
    this.onStatus(this.attempts ? 'reconnecting' : 'connecting');
    const separator = endpoint.includes('?') ? '&' : '?';
    const socket = new WebSocket(`${endpoint}${separator}profileId=${encodeURIComponent(this.profileId)}`);
    this.socket = socket;
    socket.addEventListener('open', () => {
      this.attempts = 0;
      this.onStatus('live');
      this.send({ action: 'subscribe', matchId: this.matchId });
      this.heartbeat = window.setInterval(() => this.send({ action: 'ping', matchId: this.matchId }), 5_000);
    });
    socket.addEventListener('message', (event) => {
      try {
        const message = JSON.parse(String(event.data)) as RealtimeMessage;
        if (message.type === 'match' && message.match.status === 'FINISHED' && this.heartbeat !== null) {
          window.clearInterval(this.heartbeat);
          this.heartbeat = null;
        }
        this.onMessage(message);
      } catch { /* malformed server message */ }
    });
    socket.addEventListener('close', () => {
      if (this.heartbeat !== null) window.clearInterval(this.heartbeat);
      this.heartbeat = null;
      if (this.closed) { this.onStatus('offline'); return; }
      this.attempts += 1;
      this.onStatus('reconnecting');
      this.retry = window.setTimeout(() => this.connect(), Math.min(8_000, 500 * (2 ** Math.min(this.attempts, 4))));
    });
    socket.addEventListener('error', () => socket.close());
  }

  move(revision: number, from: { file: number; rank: number }, to: { file: number; rank: number }) {
    return this.send({ action: 'move', matchId: this.matchId, revision, from, to });
  }

  resign() { return this.send({ action: 'resign', matchId: this.matchId }); }

  close() {
    this.closed = true;
    if (this.retry !== null) window.clearTimeout(this.retry);
    if (this.heartbeat !== null) window.clearInterval(this.heartbeat);
    this.socket?.close();
  }

  private send(payload: Record<string, unknown>) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(payload));
    return true;
  }
}
